import * as ExcelJS from 'exceljs';
import { ArrayUtils } from './ArrayUtils';
import { Constants } from './constants';
import { JsonUtils } from './json.utils';
import { StringUtils } from './string.utils';

export type CellValue = string | number | boolean | Date | null;

export interface SheetData {
  name: string;
  columns: string[];
  rows: Record<string, CellValue>[];
  /** Row groups produced by expanding an array of objects, used to merge parent cells */
  groups: { start: number; size: number }[];
  /** Columns created from arrays of objects (e.g. fields.key); these are never merged */
  expandedColumns: string[];
}

export interface JsonToExcelOptions {
  /** Top level keys that should not be converted into sheets */
  excludeKeys?: string[];
  /** Separator used while joining an array of primitives */
  arraySeparator?: string;
  /** Custom sheet names keyed by the top level json key, e.g. { meta: 'Summary', funds: 'Fund List' }. Unmapped keys use the key itself */
  sheetNames?: Record<string, string>;
  /** Sheet name used when the json is not an object (e.g. a top level array) */
  defaultSheetName?: string;
}

interface FlatRow {
  values: Record<string, CellValue>;
  /** Arrays of objects found in the row, already flattened, keyed by their column prefix */
  objectArrays: Record<string, Record<string, CellValue>[]>;
  columns: string[];
}

/**
 * Converts json into excel sheets:
 * - every top level key becomes a separate sheet
 * - an object becomes a single row with its keys as columns
 * - an array of objects becomes one row per element
 * - an object keyed by ids ({ id1: {...}, id2: {...} }) becomes one row per id with the id in a "key" column
 * - nested objects become "parent.child" columns
 * - an array of primitives is joined into one cell
 * - an array of objects is expanded into extra rows under "parent.child" columns, with the parent cells merged
 */
export class DocumentUtils {
  private static readonly MAX_SHEET_NAME_LENGTH = 31;
  private static readonly DEFAULT_ARRAY_SEPARATOR = `${Constants.COMMA} `;
  private static readonly DICTIONARY_KEY_COLUMN = 'key';
  private static readonly DEFAULT_SHEET_NAME = 'Sheet1';
  private static readonly MIN_COLUMN_WIDTH = 12;
  private static readonly MAX_COLUMN_WIDTH = 60;
  private static readonly COLUMN_WIDTH_PADDING = 2;
  private static readonly HEADER_FILL_COLOR = 'FFD9E1F2';
  /** Excel rows are 1 based and the first row is the header */
  private static readonly FIRST_DATA_ROW = 2;

  /**
    It produces the complete content and layout of every sheet which then is used 
    by addSheetToWorkbook to make excel.
   */
  public static jsonToSheets(json: any, options: JsonToExcelOptions = {}): SheetData[] {
    const separator = options.arraySeparator ?? DocumentUtils.DEFAULT_ARRAY_SEPARATOR;
    const excludeKeys = new Set(options.excludeKeys || []);
    const usedNames = new Set<string>();

    if (!DocumentUtils.isPlainObject(json)) {
      const sheetName = DocumentUtils.sanitizeSheetName(options.defaultSheetName || DocumentUtils.DEFAULT_SHEET_NAME, usedNames);
      return [DocumentUtils.toSheetData(sheetName, json, separator)];
    }

    return Object.keys(json)
      .filter((key) => !excludeKeys.has(key))
      .map((key) => {
        const sheet = DocumentUtils.toSheetData(key, json[key], separator);
        sheet.name = DocumentUtils.sanitizeSheetName(DocumentUtils.getSheetName(key, options.sheetNames), usedNames);
        return sheet;
      });
  }

  /** Writes one prepared sheet into the workbook: header row (bold, filled, frozen), data rows, column widths and merged parent cells */
  public static addSheetToWorkbook(workbook: ExcelJS.Workbook, sheet: SheetData): ExcelJS.Worksheet {
    const worksheet = workbook.addWorksheet(sheet.name, { views: [{ state: 'frozen', ySplit: 1 }] });
    const widths = DocumentUtils.getColumnWidths(sheet);
    worksheet.columns = sheet.columns.map((column) => ({ header: column, key: column, width: widths.get(column) }));

    const headerRow = worksheet.getRow(1);
    headerRow.font = { bold: true };
    headerRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: DocumentUtils.HEADER_FILL_COLOR } };

    sheet.rows.forEach((row) => worksheet.addRow(row));

    // Merge the parent values vertically over the rows produced by an array of objects
    const expandedColumns = new Set(sheet.expandedColumns);
    const parentColumnIndexes = sheet.columns
      .map((column, index) => (expandedColumns.has(column) ? -1 : index + 1))
      .filter((columnIndex) => columnIndex > 0);
    sheet.groups.forEach((group) => {
      const startRow = group.start + DocumentUtils.FIRST_DATA_ROW;
      const endRow = startRow + group.size - 1;
      parentColumnIndexes.forEach((columnIndex) => {
        worksheet.mergeCells(startRow, columnIndex, endRow, columnIndex);
        worksheet.getCell(startRow, columnIndex).alignment = { vertical: 'top' };
      });
    });

    return worksheet;
  }

  /** Builds an exceljs workbook from the json, with one worksheet per sheet returned by jsonToSheets */
  public static jsonToWorkbook(json: any, options: JsonToExcelOptions = {}): ExcelJS.Workbook {
    const workbook = new ExcelJS.Workbook();
    DocumentUtils.jsonToSheets(json, options).forEach((sheet) => DocumentUtils.addSheetToWorkbook(workbook, sheet));
    return workbook;
  }

  /** Entry point: converts the json into an .xlsx Blob, ready to be downloaded or uploaded */
  public static async jsonToExcelBlob(json: any, options: JsonToExcelOptions = {}): Promise<Blob> {
    const arrayBuffer = await DocumentUtils.jsonToWorkbook(json, options).xlsx.writeBuffer();
    return new Blob([arrayBuffer as ArrayBuffer], { type: Constants.XLSX_MIME_TYPE });
  }

  /** Converts the json into an .xlsx file and starts the browser download, e.g. downloadExcel(data, 'portfolio-insight') */
  public static async downloadExcel(json: any, fileName: string, options: JsonToExcelOptions = {}): Promise<void> {
    const blob = await DocumentUtils.jsonToExcelBlob(json, options);
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = /\.xlsx$/i.test(fileName) ? fileName : `${fileName}.xlsx`;
    anchor.click();
    URL.revokeObjectURL(url);
    anchor.remove();
  }

  /** True for a normal { ... } object; false for null, arrays and dates, which typeof also reports as 'object' */
  private static isPlainObject(value: any): value is Record<string, any> {
    return value !== null && typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date);
  }

  /** True for a value that fits in a single cell: string, number, boolean, date, null or undefined */
  private static isPrimitive(value: any): boolean {
    return value === null || value === undefined || ['string', 'number', 'boolean'].includes(typeof value) || value instanceof Date;
  }

  /** True when every item of the array fits in a single cell, e.g. ['rating', 'topHoldings'] */
  private static isPrimitiveArray(value: any): value is CellValue[] {
    return Array.isArray(value) && value.every((item) => DocumentUtils.isPrimitive(item));
  }

  /** True for a non empty array whose items are all objects, e.g. [{ upto: '1Y', pct: 1 }] */
  private static isObjectArray(value: any): value is Record<string, any>[] {
    return ArrayUtils.isNotEmpty(value) && value.every((item: any) => DocumentUtils.isPlainObject(item));
  }

  /**
   * An object keyed by ids, e.g. { "INF917K01QC7": {...}, "INF769K01010": {...} }.
   * It needs at least two entries sharing a key, so a plain nested object like
   * { address: { city }, contact: { phone } } still stays a single row.
   */
  private static isDictionaryOfObjects(value: any): value is Record<string, Record<string, any>> {
    if (!DocumentUtils.isPlainObject(value) || JsonUtils.isEmpty(value)) {
      return false;
    }
    const entries = Object.values(value);
    if (entries.length < 2 || !entries.every((entry) => DocumentUtils.isPlainObject(entry))) {
      return false;
    }
    const [first, ...others] = entries.map((entry) => Object.keys(entry));
    return first.some((key) => others.every((keys) => keys.includes(key)));
  }

  /** Joins an array of primitives into one cell value, e.g. ['rating', 'topHoldings'] -> 'rating, topHoldings' */
  private static joinPrimitiveArray(values: CellValue[], separator: string): string {
    const strings = (values || [])
      .filter((value) => value !== null && value !== undefined)
      .map((value) => (value instanceof Date ? value.toISOString() : String(value)));
    return StringUtils.join(strings, separator);
  }

  /** Excel sheet names are limited to 31 characters and cannot contain : \ / ? * [ ] */
  private static sanitizeSheetName(name: string, usedNames: Set<string>): string {
    const base = (String(name || 'Sheet').replace(/[:\\/?*\[\]]/g, '_').trim() || 'Sheet').substring(0, DocumentUtils.MAX_SHEET_NAME_LENGTH);
    let sheetName = base;
    let counter = 1;
    while (usedNames.has(sheetName.toLowerCase())) {
      const suffix = `_${counter++}`;
      sheetName = base.substring(0, DocumentUtils.MAX_SHEET_NAME_LENGTH - suffix.length) + suffix;
    }
    usedNames.add(sheetName.toLowerCase());
    return sheetName;
  }

  /** Returns the custom sheet name for a json key when one is provided, otherwise the key itself */
  private static getSheetName(key: string, sheetNames?: Record<string, string>): string {
    const customName = sheetNames?.[key] ?? Constants.EMPTY_STRING;
    return StringUtils.isNotEmpty(customName) ? customName : key;
  }

  /** Picks the right conversion for any value found against a top level key */
  private static toSheetData(name: string, value: any, separator: string): SheetData {
    if (Array.isArray(value)) {
      return DocumentUtils.arrayToSheetData(name, value, separator);
    }
    if (DocumentUtils.isDictionaryOfObjects(value)) {
      return DocumentUtils.dictionaryToSheetData(name, value, separator);
    }
    if (DocumentUtils.isPlainObject(value)) {
      return DocumentUtils.rowsToSheetData(name, [value], separator);
    }
    return DocumentUtils.rowsToSheetData(name, [{ [name]: value }], separator);
  }

  /** Array of objects -> keys are columns and every element is a row */
  private static arrayToSheetData(name: string, array: any[], separator: string): SheetData {
    // Items that are not objects (e.g. a top level array of strings) go into a single column named after the sheet
    const items = (array || []).map((item) =>
      DocumentUtils.isPlainObject(item) ? item : { [name]: Array.isArray(item) ? JSON.stringify(item) : item },
    );
    return DocumentUtils.rowsToSheetData(name, items, separator);
  }

  /**
   * An object keyed by ids would otherwise become a single very wide row, so every entry becomes a row
   * with its id in the "key" column. When the entries already have a "key" field, the id column is renamed
   * (key_1, key_2, ...) so neither value is lost.
   *
   * Example:
   *   { A: { age: 30 }, B: { age: 25 } }
   *   becomes
   *   key | age
   *   A   | 30
   *   B   | 25
   */
  private static dictionaryToSheetData(name: string, dictionary: Record<string, Record<string, any>>, separator: string): SheetData {
    const entries = Object.values(dictionary);
    let keyColumn = DocumentUtils.DICTIONARY_KEY_COLUMN;
    for (let counter = 1; entries.some((entry) => keyColumn in entry); counter++) {
      keyColumn = `${DocumentUtils.DICTIONARY_KEY_COLUMN}_${counter}`;
    }
    const rows = Object.keys(dictionary).map((key) => ({ [keyColumn]: key, ...dictionary[key] }));
    return DocumentUtils.rowsToSheetData(name, rows, separator);
  }

  /**
   * Shared core of every conversion: each object is flattened, expanded into one or more rows
   * and its columns are added to the header in the order they are first seen.
   *
   * Example:
   *   [ { name: 'A', tags: [{ id: 1 }, { id: 2 }] }, { name: 'B' } ]
   *   becomes
   *   name | tags.id
   *   A    | 1        <- "A" is merged over these two rows (group { start: 0, size: 2 })
   *        | 2
   *   B    |
   */
  private static rowsToSheetData(name: string, items: Record<string, any>[], separator: string): SheetData {
    const sheet: SheetData = { name, columns: [], rows: [], groups: [], expandedColumns: [] };
    const seenColumns = new Set<string>();
    const seenExpandedColumns = new Set<string>();

    items.forEach((item) => {
      const flatRow = DocumentUtils.flattenObject(item, separator);
      const rows = DocumentUtils.expandObjectArrays(flatRow);
      if (rows.length > 1) {
        sheet.groups.push({ start: sheet.rows.length, size: rows.length });
      }
      sheet.rows.push(...rows);
      DocumentUtils.addColumns(sheet.columns, seenColumns, flatRow.columns);
      DocumentUtils.addColumns(
        sheet.expandedColumns,
        seenExpandedColumns,
        Object.values(flatRow.objectArrays).flatMap((elements) => elements.flatMap((element) => Object.keys(element))),
      );
    });

    return sheet;
  }

  /**
   * Flattens a single object into column -> value pairs.
   * Nested objects become "parent.child" columns, arrays of primitives are joined
   * and arrays of objects are collected separately so they can be expanded into rows.
   * Empty values (null, undefined, '', [], {}) are skipped; 0 and false are kept.
   *
   * Example:
   *   { name: 'A', city: { pin: 1 }, langs: ['en', 'hi'], tags: [{ id: 1 }], note: null }
   *   becomes
   *   values:       { name: 'A', 'city.pin': 1, langs: 'en, hi' }
   *   objectArrays: { tags: [{ 'tags.id': 1 }] }
   *   columns:      ['name', 'city.pin', 'langs', 'tags.id']
   */
  private static flattenObject(obj: Record<string, any>, separator: string, prefix = ''): FlatRow {
    const flatRow: FlatRow = { values: {}, objectArrays: {}, columns: [] };
    const seenColumns = new Set<string>();

    Object.keys(obj || {}).forEach((key) => {
      const column = prefix ? `${prefix}${Constants.DOT}${key}` : key;
      const value = obj[key];
      // Empty values do not create a column (0 and false are valid values, so they are kept)
      if (
        value === null ||
        value === undefined ||
        (typeof value === 'string' && StringUtils.isEmpty(value)) ||
        (Array.isArray(value) && ArrayUtils.isEmpty(value)) ||
        (DocumentUtils.isPlainObject(value) && JsonUtils.isEmpty(value))
      ) {
        return;
      }

      if (DocumentUtils.isObjectArray(value)) {
        const elements = value.map((element) => DocumentUtils.flattenNestedElement(element, separator, column));
        flatRow.objectArrays[column] = elements;
        DocumentUtils.addColumns(flatRow.columns, seenColumns, elements.flatMap((element) => Object.keys(element)));
      } else if (Array.isArray(value)) {
        flatRow.values[column] = DocumentUtils.isPrimitiveArray(value) ? DocumentUtils.joinPrimitiveArray(value, separator) : JSON.stringify(value);
        DocumentUtils.addColumns(flatRow.columns, seenColumns, [column]);
      } else if (DocumentUtils.isPlainObject(value)) {
        const nested = DocumentUtils.flattenObject(value, separator, column);
        Object.assign(flatRow.values, nested.values);
        Object.assign(flatRow.objectArrays, nested.objectArrays);
        DocumentUtils.addColumns(flatRow.columns, seenColumns, nested.columns);
      } else {
        flatRow.values[column] = value;
        DocumentUtils.addColumns(flatRow.columns, seenColumns, [column]);
      }
    });

    return flatRow;
  }

  /** Flattens one item of an array of objects under the array's prefix, e.g. { upto: '1Y' } -> { 'exitLoad.upto': '1Y' } */
  private static flattenNestedElement(element: Record<string, any>, separator: string, prefix: string): Record<string, CellValue> {
    const flatRow = DocumentUtils.flattenObject(element, separator, prefix);
    // Arrays of objects nested inside an array element are kept as JSON text instead of expanding again
    Object.keys(flatRow.objectArrays).forEach((column) => {
      flatRow.values[column] = JSON.stringify(flatRow.objectArrays[column]);
    });
    return flatRow.values;
  }

  /**
   * Turns a row containing arrays of objects into multiple rows.
   * Element i of every array goes into row i, under "arrayKey.objectKey" columns.
   */
  private static expandObjectArrays(flatRow: FlatRow): Record<string, CellValue>[] {
    const arrays = Object.values(flatRow.objectArrays);
    const size = arrays.reduce((max, array) => Math.max(max, array.length), 1);
    const rows: Record<string, CellValue>[] = [];

    for (let index = 0; index < size; index++) {
      const row: Record<string, CellValue> = index === 0 ? { ...flatRow.values } : {};
      arrays.forEach((array) => Object.assign(row, array[index] || {}));
      rows.push(row);
    }
    return rows;
  }

  /** Column width fits the longest header or value, within the min and max width, in a single pass over the cells */
  private static getColumnWidths(sheet: SheetData): Map<string, number> {
    const lengths = new Map(sheet.columns.map((column) => [column, column.length]));
    sheet.rows.forEach((row) => {
      Object.keys(row).forEach((column) => {
        const length = String(row[column] ?? Constants.EMPTY_STRING).length;
        if (length > (lengths.get(column) ?? 0)) {
          lengths.set(column, length);
        }
      });
    });

    const widths = new Map<string, number>();
    lengths.forEach((length, column) => {
      const width = length + DocumentUtils.COLUMN_WIDTH_PADDING;
      widths.set(column, Math.min(DocumentUtils.MAX_COLUMN_WIDTH, Math.max(DocumentUtils.MIN_COLUMN_WIDTH, width)));
    });
    return widths;
  }

  /** Appends columns that are not already present, keeping the order they are first seen */
  private static addColumns(columns: string[], seenColumns: Set<string>, newColumns: string[]): void {
    newColumns.forEach((column) => {
      if (!seenColumns.has(column)) {
        seenColumns.add(column);
        columns.push(column);
      }
    });
  }
}
